import { callbackMethods } from '../../constants';
import { emptyObjectToUndefined } from '../helperFunctions';
import {
  optionalMinMaxLength,
  zodBankAccountSchema,
  zodCurrencySchema,
  zodLocationSchema,
  zodPhoneSchema,
  zodRedirectSchema,
  zodStringOptionSchema,
} from '../Validators';
import z from 'zod';

export const createNewSupplierSchema = z.object(
  {
    supplierName: z
      .string({
        required_error: 'Supplier Name is required',
        invalid_type_error: 'Expected String for Supplier Name.',
      })
      .trim()
      .min(1, { message: 'Supplier Name cannot be empty' }),
    email: z
      .string({
        required_error: 'Email ID is required',
        invalid_type_error: 'Expected string for email ID.',
      })
      .trim()
      .email({ message: 'Invalid email format' })
      .max(254, { message: 'Email too long' }),
    phone: emptyObjectToUndefined(zodPhoneSchema.optional()),
    companyCodes: z
      .array(zodStringOptionSchema, {
        required_error: 'Tax IDs are required',
        invalid_type_error: 'Tax IDs must be an array of objects',
      })
      .optional(),
    description: optionalMinMaxLength(1, 1000, 'Description'),
    locations: z
      .array(zodLocationSchema, {
        required_error: 'Locations are required',
        invalid_type_error: 'Expected array of object for location.',
      })
      .min(1, { message: 'Please add at least one Location.' }),
    bankAccounts: z
      .array(zodBankAccountSchema, {
        required_error: 'Bank accounts are required',
        invalid_type_error:
          'Bank accounts must be an array of valid bank account objects',
      })
      .min(1, { message: 'At least one bank account is required' }),
    currency: zodCurrencySchema,
    invoicePrefix: optionalMinMaxLength(1, 200, 'Invoice Prefix'),
    globalRedirects: z
      .boolean({ invalid_type_error: 'Expected boolean for globalRedirects ' })
      .optional(),
    redirects: zodRedirectSchema.optional(),
    serverCallback: z
      .boolean({ invalid_type_error: 'Expected boolean for serverCallback ' })
      .optional(),
    serverCallbackEndpoints: zodRedirectSchema.optional(),
    serverCallbackMethod: z
      .enum(callbackMethods, {
        required_error: 'Server Callback Method is required.',
        invalid_type_error: 'Expected string for Server Callback Method.',
        message: `Server Callback Method must be one of ${callbackMethods.join(
          ', ',
        )}`,
      })
      .optional(),
  },
  {
    required_error: 'Please provide all required fields',
    invalid_type_error: 'Please provide valid data type',
  },
);

export const createNewSupplierFirstStepSchema = z.object({
  supplierName: z
    .string({
      required_error: 'Supplier Name is required',
      invalid_type_error: 'Expected String for Supplier Name.',
    })
    .trim()
    .min(1, { message: 'Supplier Name cannot be empty' }),
  email: z
    .string({
      required_error: 'Email ID is required',
      invalid_type_error: 'Expected string for email ID.',
    })
    .trim()
    .email({ message: 'Invalid email format' })
    .max(254, { message: 'Email too long' }),
  phone: emptyObjectToUndefined(zodPhoneSchema.optional()),
  companyCodes: z
    .array(zodStringOptionSchema, {
      required_error: 'Tax IDs are required',
      invalid_type_error: 'Tax IDs must be an array of objects',
    })
    .optional(),
  description: optionalMinMaxLength(5, undefined, 'Description'),
  currency: zodCurrencySchema,
  invoicePrefix: optionalMinMaxLength(1, 500, 'Invoice Prefix'),
});

export const createNewSupplierSecondStepSchema = z.object({
  locations: z
    .array(zodLocationSchema, {
      required_error: 'Locations are required',
      invalid_type_error:
        'Locations must be an array of valid location objects',
    })
    .min(1, { message: 'At least one location is required' }),
});
export const createNewSupplierThirdStepSchema = z.object({
  bankAccounts: z
    .array(zodBankAccountSchema, {
      required_error: 'Bank accounts are required',
      invalid_type_error:
        'Bank accounts must be an array of valid bank account objects',
    })
    .min(1, { message: 'At least one bank account is required' }),
});

export const createNewSupplierFourthStepSchema = z
  .object({
    globalRedirects: z
      .boolean({ invalid_type_error: 'Expected boolean for globalRedirects ' })
      .optional(),
    redirects: zodRedirectSchema.optional(),
    serverCallback: z
      .boolean({ invalid_type_error: 'Expected boolean for serverCallback ' })
      .optional(),
    serverCallbackEndpoints: zodRedirectSchema.optional(),
    serverCallbackMethod: z
      .enum(callbackMethods, {
        required_error: 'Server Callback Method is required.',
        invalid_type_error: 'Expected string for Server Callback Method.',
        message: `Server Callback Method must be one of ${callbackMethods.join(
          ', ',
        )}`,
      })
      .optional(),
  })
  .superRefine((value, ctx) => {
    if (value?.globalRedirects && !Object.keys(value?.redirects || {}).length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['redirects'],
        message: 'Redirects are required if globalRedirects is true',
      });
    }

    if (
      value?.serverCallback &&
      !Object.keys(value?.serverCallbackEndpoints || {}).length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['serverCallbackEndpoints'],
        message:
          'Server Callback endpoints  are required if server Callback is true',
      });
    }
    if (value?.serverCallback && !value?.serverCallbackMethod) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['serverCallbackMethod'],
        message:
          'Server Callback Method is required if server Callback is true',
      });
    }
  });
